import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { meetingDataService, MeetingData } from '../services/meetingDataService';
import { useAppStore } from '../state/store';
import { appInsights } from '../services/appInsights';
import { logDebug } from '../utils/errorLogger';

const MeetingPage: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const [meetingData, setMeetingData] = useState<MeetingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { loadFromJson } = useAppStore();

  useEffect(() => {
    const loadMeetingData = async () => {
      if (!meetingId) {
        navigate('/404', { replace: true });
        return;
      }

      // Validate meeting ID format
      if (!meetingDataService.isValidMeetingId(meetingId)) {
        navigate('/404', { replace: true });
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        logDebug(`Loading meeting: ${meetingId}`);
        
        // Set store loading state
        useAppStore.getState().setLoading(true);
        
        // Track meeting page view
        appInsights.trackPageView(`Meeting: ${meetingId}`, `/meeting/${meetingId}`);
        
        const data = await meetingDataService.loadMeeting(meetingId);
        
        setMeetingData(data);
        
        // Update the store with the loaded data (convert MeetingData to DistillOutput format)
        const distillOutput = {
          meeting_brief: data.meeting_brief,
          key_points: data.key_points,
          actions: data.actions,
          themes: data.themes,
          clarifications: [], // Default empty array for missing properties
          metrics: {
            total_key_points: data.key_points?.length || 0,
            total_actions: data.actions?.length || 0,
            total_themes: data.themes?.length || 0,
            confidence_score_avg: 0.8 // Default confidence score
          }
        };
        loadFromJson(distillOutput, meetingId);
        
        // Track successful meeting load
        appInsights.trackEvent('MeetingLoaded', {
          meetingId,
          keyPointsCount: data.key_points?.length || 0,
          actionsCount: data.actions?.length || 0,
          themesCount: data.themes?.length || 0
        });
        
        logDebug('Meeting data loaded successfully');
        
      } catch (err) {
        // Don't log to Application Insights - meeting not found is normal user behavior
        // Backend API will log server errors
        console.error('Failed to load meeting:', err);
        
        // For meeting not found or any error, redirect to 404
        // This prevents showing error messages on top of the main UI
        navigate('/404', { replace: true });
        return;
      } finally {
        setLoading(false);
      }
    };

    loadMeetingData();
  }, [meetingId, loadFromJson, navigate]);

  // Note: Authentication is handled at the App level, so we don't need to check here

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading meeting...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        flexDirection="column"
        gap={2}
        px={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            {error.includes('not found') || error.includes('expired') 
              ? 'Meeting Not Found' 
              : 'Error Loading Meeting'
            }
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Meeting ID: {meetingId}
        </Typography>
      </Box>
    );
  }

  if (!meetingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Alert severity="warning">
          No meeting data available
        </Alert>
      </Box>
    );
  }

  // This component only handles data loading - UI is rendered by parent
  return null;
};

export default MeetingPage;
